import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, X, Send, Bot } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Message {
  id: string;
  role: 'user' | 'agent';
  content: string;
  timestamp: Date;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL ?? '';

const WELCOME_MESSAGE: Message = {
  id: 'welcome',
  role: 'agent',
  content:
    "Hi there! I'm the SMPL Digital assistant. Tell me about your business and what you're trying to achieve — I'll point you in the right direction.",
  timestamp: new Date(),
};

function generateChatId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older environments
  return `${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface TypingIndicatorProps {}
const TypingIndicator: React.FC<TypingIndicatorProps> = () => (
  <div className="flex justify-start">
    <div className="flex items-start gap-2">
      <div className="w-7 h-7 rounded-full bg-primary-indigo/10 dark:bg-primary-indigo/20 flex items-center justify-center shrink-0">
        <Bot className="w-4 h-4 text-primary-indigo dark:text-indigo-400" aria-hidden="true" />
      </div>
      <div className="px-4 py-3 bg-neutral-100 dark:bg-neutral-dark-200 rounded-2xl rounded-tl-sm">
        <div className="flex gap-1 items-center h-4" aria-label="Agent is typing">
          <span className="typing-dot w-1.5 h-1.5 bg-neutral-400 dark:bg-neutral-dark-500 rounded-full" style={{ animationDelay: '0ms' }} />
          <span className="typing-dot w-1.5 h-1.5 bg-neutral-400 dark:bg-neutral-dark-500 rounded-full" style={{ animationDelay: '160ms' }} />
          <span className="typing-dot w-1.5 h-1.5 bg-neutral-400 dark:bg-neutral-dark-500 rounded-full" style={{ animationDelay: '320ms' }} />
        </div>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Markdown renderer — handles the subset an AI sales agent typically outputs:
// **bold**, *italic*, `code`, bullet lists (- / *), numbered lists, paragraphs.
// Kept inline to avoid adding a dependency for a small widget.
// ---------------------------------------------------------------------------

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  // Split on **bold**, *italic*, `code`, [text](url) markers
  const tokens = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g);
  return tokens.map((token, i) => {
    const k = `${keyPrefix}-${i}`;
    if (token.startsWith('**') && token.endsWith('**'))
      return <strong key={k}>{token.slice(2, -2)}</strong>;
    if (token.startsWith('*') && token.endsWith('*'))
      return <em key={k}>{token.slice(1, -1)}</em>;
    if (token.startsWith('`') && token.endsWith('`'))
      return <code key={k} className="bg-black/10 dark:bg-white/10 px-1 rounded font-mono text-xs">{token.slice(1, -1)}</code>;
    const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch)
      return <a key={k} href={linkMatch[2]} className="underline underline-offset-2 hover:opacity-80 transition-opacity">{linkMatch[1]}</a>;
    return <React.Fragment key={k}>{token}</React.Fragment>;
  });
}

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const output: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Bullet list block
    if (/^[-*]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s/, ''));
        i++;
      }
      output.push(
        <ul key={`ul-${i}`} className="list-disc pl-4 space-y-0.5 my-1">
          {items.map((item, j) => (
            <li key={j}>{renderInline(item, `ul-${i}-${j}`)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list block
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      output.push(
        <ol key={`ol-${i}`} className="list-decimal pl-4 space-y-0.5 my-1">
          {items.map((item, j) => (
            <li key={j}>{renderInline(item, `ol-${i}-${j}`)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Blank line — small spacer
    if (line.trim() === '') {
      output.push(<div key={`br-${i}`} className="h-1.5" aria-hidden="true" />);
      i++;
      continue;
    }

    // Regular text line
    output.push(
      <p key={`p-${i}`} className="leading-relaxed">
        {renderInline(line, `p-${i}`)}
      </p>
    );
    i++;
  }

  return <>{output}</>;
}

// ---------------------------------------------------------------------------

interface ChatMessageProps {
  message: Message;
}
const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[82%] ${isUser ? '' : 'flex items-start gap-2'}`}>
        {!isUser && (
          <div className="w-7 h-7 rounded-full bg-primary-indigo/10 dark:bg-primary-indigo/20 flex items-center justify-center shrink-0 mt-0.5" aria-hidden="true">
            <Bot className="w-4 h-4 text-primary-indigo dark:text-indigo-400" />
          </div>
        )}
        <div>
          <div
            className={`px-3.5 py-2.5 rounded-2xl text-sm break-words ${
              isUser
                ? 'bg-primary-rose text-white rounded-tr-sm'
                : 'bg-neutral-100 dark:bg-neutral-dark-200 text-neutral-800 dark:text-neutral-dark-800 rounded-tl-sm'
            }`}
          >
            {isUser ? message.content : renderMarkdown(message.content)}
          </div>
          <div className={`text-xs text-neutral-400 dark:text-neutral-dark-500 mt-1 ${isUser ? 'text-right' : 'text-left'}`}>
            {formatTime(message.timestamp)}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const AgentChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isTerminated, setIsTerminated] = useState(false);

  // chatId persists for the browser session so n8n can maintain context
  const [chatId] = useState<string>(() => {
    const stored = sessionStorage.getItem('smpl_chat_id');
    if (stored) return stored;
    const id = generateChatId();
    sessionStorage.setItem('smpl_chat_id', id);
    return id;
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to newest message whenever messages list or loading state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus the input when the panel opens and after each agent response
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [isOpen]);


  // Close on Escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  // Auto-resize textarea as user types
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 112)}px`;
  };

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');

    // Reset textarea height after clearing
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }

    setIsLoading(true);

    try {
      if (!WEBHOOK_URL) {
        throw new Error('REACT_APP_N8N_WEBHOOK_URL is not set');
      }

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const secret = process.env.REACT_APP_N8N_WEBHOOK_SECRET;
      if (secret) headers['x-webhook-secret'] = secret;

      const res = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          chatId,
          message: text,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!res.ok) throw new Error(`Webhook returned HTTP ${res.status}`);

      const data = await res.json();

      // n8n returns { output, terminate } with structured output mode,
      // or { output: "string" } in standard mode. Unwrap either shape.
      const rawOutput = data?.output ?? data?.response ?? data?.message ?? data?.text ?? data;
      const responseText: string =
        typeof rawOutput === 'string'
          ? rawOutput
          : typeof rawOutput?.output === 'string'
          ? rawOutput.output
          : "Sorry, I received an unexpected response. Please try again or use the contact form.";

      const shouldTerminate = data?.terminate === true || rawOutput?.terminate === true;

      setHasError(false);
      if (shouldTerminate) setIsTerminated(true);
      setMessages(prev => [
        ...prev,
        { id: `agent-${Date.now()}`, role: 'agent', content: String(responseText), timestamp: new Date() },
      ]);
    } catch (err) {
      console.error('[AgentChat] Webhook error:', err);
      setHasError(true);
      setMessages(prev => [
        ...prev,
        {
          id: `agent-err-${Date.now()}`,
          role: 'agent',
          content:
            "I'm having trouble connecting right now. You can reach the team directly at [hello@smpldigital.co.uk](mailto:hello@smpldigital.co.uk) or via the [contact form](/contact).",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, chatId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const canSend = input.trim().length > 0 && !isLoading;

  return (
    <>
      {/* ─── Chat Panel ──────────────────────────────────────────────── */}
      {isOpen && (
        <div
          id="agent-chat-panel"
          className="chat-panel-enter fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-96 max-h-[calc(100vh-8rem)] flex flex-col bg-white dark:bg-neutral-dark-100 rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-dark-300 overflow-hidden"
          role="dialog"
          aria-label="SMPL Digital chat assistant"
          aria-modal="true"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-primary-indigo text-white shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0" aria-hidden="true">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-sm leading-tight">SMPL Digital Agent</p>
                {hasError ? (
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-rose animate-ping absolute" aria-hidden="true" />
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-rose relative" aria-hidden="true" />
                    <Link
                      to="/contact"
                      className="text-xs text-primary-rose font-medium hover:text-white transition-colors"
                      aria-label="Agent unavailable — use contact form"
                    >
                      Agent unavailable — contact us
                    </Link>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" aria-hidden="true" />
                    <span className="text-xs text-white/80">Online</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>

          {/* Message history */}
          <div
            className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0"
            aria-live="polite"
            aria-relevant="additions"
          >
            {messages.map(msg => (
              <ChatMessage key={msg.id} message={msg} />
            ))}

            {isLoading && <TypingIndicator />}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} aria-hidden="true" />
          </div>

          {/* Input area */}
          <div className="px-4 py-3 border-t border-neutral-200 dark:border-neutral-dark-300 shrink-0 bg-white dark:bg-neutral-dark-100">
            {isTerminated ? (
              <div className="text-center py-2 space-y-2">
                <p className="text-xs text-neutral-500 dark:text-neutral-dark-500">We'll be in touch soon.</p>
                <div className="flex gap-2 justify-center">
                  <Link
                    to="/contact"
                    className="inline-block px-3 py-1.5 bg-primary-indigo text-white text-xs font-medium rounded-lg hover:bg-primary-indigo/90 transition-colors"
                  >
                    Contact form
                  </Link>
                  <Link
                    to="/services"
                    className="inline-block px-3 py-1.5 border border-neutral-200 dark:border-neutral-dark-400 text-neutral-700 dark:text-neutral-dark-700 text-xs font-medium rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-dark-200 transition-colors"
                  >
                    Our services
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message…"
                    rows={1}
                    className="flex-1 resize-none rounded-xl border border-neutral-200 dark:border-neutral-dark-400 bg-neutral-50 dark:bg-neutral-dark-200 text-neutral-900 dark:text-neutral-dark-900 placeholder-neutral-400 dark:placeholder-neutral-dark-500 text-sm px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-indigo dark:focus:ring-indigo-400 focus:border-transparent transition-colors max-h-28 overflow-y-auto"
                    aria-label="Chat message"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!canSend}
                    className="p-2.5 bg-primary-indigo hover:bg-primary-indigo/90 disabled:bg-neutral-300 dark:disabled:bg-neutral-dark-400 text-white disabled:text-neutral-500 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-primary-indigo focus:ring-offset-2 shrink-0"
                    aria-label="Send message"
                  >
                    <Send className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
                <p className="text-xs text-neutral-400 dark:text-neutral-dark-500 mt-2 text-center select-none">
                  <kbd className="font-mono">Enter</kbd> to send &middot; <kbd className="font-mono">Shift+Enter</kbd> for new line
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── Floating Toggle Button ───────────────────────────────────── */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="fixed bottom-6 right-4 sm:right-6 z-50 w-14 h-14 rounded-full bg-primary-indigo hover:bg-primary-indigo/90 text-white shadow-lg hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-indigo focus:ring-offset-2 flex items-center justify-center"
        aria-label={isOpen ? 'Close chat assistant' : 'Open chat assistant'}
        aria-expanded={isOpen}
        aria-controls="agent-chat-panel"
      >
        {/* Icon crossfade: MessageCircle ↔ X */}
        <span className={`absolute transition-all duration-200 ${isOpen ? 'opacity-0 scale-75' : 'opacity-100 scale-100'}`} aria-hidden="true">
          <MessageCircle className="w-6 h-6" />
        </span>
        <span className={`absolute transition-all duration-200 ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} aria-hidden="true">
          <X className="w-6 h-6" />
        </span>

        {/* Attention pulse ring — only shown when closed */}
        {!isOpen && (
          <span className="absolute inset-0 rounded-full bg-primary-indigo animate-ping opacity-25 pointer-events-none" aria-hidden="true" />
        )}
      </button>
    </>
  );
};

export default AgentChat;
